import React from 'react'
import { Col, Row, Button } from 'react-bootstrap';
import ReactDayPickerInput from 'react-day-picker/DayPickerInput';
//import Multiselect from 'react-multi-select-component';
import BootstrapTable, { ColumnDescription } from 'react-bootstrap-table-next';
import { Scrollbars } from 'react-custom-scrollbars';

import Navigation from '../../components/navigation/Navigation';
import { ErrorMessage, Loading } from '../../components/visuals/Visuals';
import Badge from '../../components/Badge';
import Table from '../../components/Table';
import ScrollContainer from '../../components/ScrollContainer';
import Multiselect from '../../components/Multiselect';

import { Prognosis } from '../../lib/utilities';
import { months_long_se, weekdays_long_se, weekdays_short_se } from '../../lib/constants';
import DataAccess from '../../lib/dataAccess/DataAccess';
import { IForecastAggregatedOverview } from '../../lib/types/types';

import { yearGradeOptions } from '../../lib/constants';

interface Props {
    role: string
}

interface State {
    error: string | null,
    isLoading: boolean,
    filtersModified: boolean,
    courseColumns: ColumnDescription<any, any>[],
    //options
    courseOptions: { value:string, label:string }[],
    classOptions: { value:number, label:string, schoolId:number }[],
    schoolOptions: { value:number, label:string }[],
    teacherOptions: { value:number, label:string }[],
    yearGradeOptions: { value:number, label:string }[],
    programOptions: { value:string, label:string }[],
    //filters
    startDate?: Date,
    endDate?: Date,
    courseIds: { value:string, label:string }[],
    classIds: { value:number, label:string }[],
    schoolIds: { value:number, label:string }[],
    teachers: { value:number, label:string }[],
    yearGrades: { value:number, label:string }[],
    programs: { value:string, label:string }[],

    data: any[]
}

export default class AggregatedTablePage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.getFilterOptions = this.getFilterOptions.bind(this);
        this.fetchData = this.fetchData.bind(this);

        this.removeCourseIdFilter = this.removeCourseIdFilter.bind(this);
        this.removeClassIdFilter = this.removeClassIdFilter.bind(this);
        this.removeSchoolIdFilter = this.removeSchoolIdFilter.bind(this);
        this.removeYearGradeFilter = this.removeYearGradeFilter.bind(this);
        this.removeProgramFilter = this.removeProgramFilter.bind(this);
        this.removeTeacherFilter = this.removeTeacherFilter.bind(this);

        this.state = {
            error: null,
            isLoading: true,
            filtersModified: false,
            courseColumns: [],
            //options
            courseOptions: [],
            classOptions: [],
            schoolOptions: [],
            yearGradeOptions: yearGradeOptions,
            programOptions: [],
            teacherOptions: [],
            //filters
            startDate: undefined,
            endDate: undefined,
            courseIds: [],
            classIds: [],
            schoolIds: [],
            yearGrades: [],
            programs: [],
            teachers: [],

            data: []
        }
    }

    componentDidMount() {
        this.getFilterOptions();
    }

    async getFilterOptions() {
        this.setState({ isLoading: true, error: null });

        try {
            let coursesPromise = DataAccess.General.getAllCourses();
            let classesPromise = DataAccess.General.getAllClasses();
            let schoolPromise = DataAccess.General.getAllSchools();
            let teacherPromise = DataAccess.General.getTeachers();
            let programPromise = DataAccess.General.getAllPrograms();

            let courses = await coursesPromise;
            let classes = await classesPromise;
            let schools = await schoolPromise;
            let teachers = await teacherPromise;
            let programs = await programPromise;

            const courseObject = courses.reduce((acc: any, course) => {
                acc[course.name] = course;
                return acc;
            }, {});
            const uniqueCourses = Object.keys(courseObject).map(key => courseObject[key]);
            
            const programObject = [{id: -1, code: 'GR', name: 'Grundskola'}].concat(programs).reduce((acc: any, program) => {
                acc[program.code] = program;
                return acc;
            }, {});
            const uniquePrograms = Object.keys(programObject).map(key => programObject[key]).filter(p => p.name != "undefined");

            let courseOptions = uniqueCourses.map(c => ({value: c.name, label: c.displayName}));
            let classOptions = classes.filter(c => c.school != null)
                                .map(c => ({value: c.id, label: `${c.name}, ${c.school?.name}`, schoolId: (c.school?.id || -1)}));
            let schoolOptions = schools.map(s => ({value: s.id, label: s.name}));
            let teacherOptions = teachers.map(t => ({value: t.id, label: `${t.fullname}, ${t.externalId}`}))
            let programOptions = uniquePrograms.map(p => ({value: p.code, label: p.name}))

            this.setState({ error: null, isLoading: false, courseOptions, classOptions, schoolOptions, teacherOptions, programOptions })
        }
        catch(e) {
            this.setState({ error: e, isLoading: false });
        }
    }

    removeCourseIdFilter(index: number) {
        this.setState(({courseIds}) => ({ courseIds: [...courseIds.slice(0, index), ...courseIds.slice(index + 1)], filtersModified: true }));
    }

    removeClassIdFilter(index: number) {
        this.setState(({classIds}) => ({ classIds: [...classIds.slice(0, index), ...classIds.slice(index + 1)], filtersModified: true }));
    }

    removeSchoolIdFilter(index: number) {
        this.setState(({schoolIds}) => ({ schoolIds: [...schoolIds.slice(0, index), ...schoolIds.slice(index + 1)], filtersModified: true }));
    }
    
    removeYearGradeFilter(index: number) {
        this.setState(({yearGrades}) => ({ yearGrades: [...yearGrades.slice(0, index), ...yearGrades.slice(index + 1)], filtersModified: true }));
    }
    
    removeProgramFilter(index: number) {
        this.setState(({programs}) => ({ programs: [...programs.slice(0, index), ...programs.slice(index + 1)], filtersModified: true }));
    }
    
    removeTeacherFilter(index: number) {
        this.setState(({teachers}) => ({ teachers: [...teachers.slice(0, index), ...teachers.slice(index + 1)], filtersModified: true }));
    }

    async fetchData() {
        this.setState({isLoading: true, filtersModified: false});
        try {
            const { startDate, endDate, courseIds, classIds, schoolIds, programs, teachers, yearGrades } = this.state;

            if(startDate == undefined || endDate == undefined) {
                throw('Datumintervall saknas...');
            }

            let db_data : IForecastAggregatedOverview[] = await DataAccess.Statistics.GetForecastAggregatedOverview(
                {
                    startDate, endDate, 
                    classIds: classIds.map(e => e.value), 
                    courseNames: courseIds.map(e => e.value), 
                    schoolIds: schoolIds.map(e => e.value),
                    programNames: programs.map(p => p.value),
                    teacherIds: teachers.map(t => t.value),
                    yearGrades: yearGrades.map(y => y.value)
                });

            let courses: {[id:string]: any} = {};

            db_data.forEach((elem: IForecastAggregatedOverview) => {
                courses[elem.courseName] = {
                    ...courses[elem.courseName],
                    id: elem.courseName,
                    displayName: elem.courseDisplayName,
                    [elem.gender]: {
                        prognosis_1: elem.prognosis_1,
                        prognosis_2: elem.prognosis_2,
                        prognosis_3: elem.prognosis_3,
                        prognosis_4: elem.prognosis_4,
                        prognosis_5: elem.prognosis_5
                    }
                }
            });

            const prognosisArr = [5, 1, 2, 3, 4];

            for(const courseId in courses) {
                const course = courses[courseId];

                const prognoses_male_amount: {[id:number]: number} = {
                    1: course['M']?.prognosis_1 || 0,
                    2: course['M']?.prognosis_2 || 0,
                    3: course['M']?.prognosis_3 || 0,
                    4: course['M']?.prognosis_4 || 0,
                    5: course['M']?.prognosis_5 || 0
                }
                const prognosis_tot_amount_male = prognosisArr.reduce((acc: number, key) => acc + prognoses_male_amount[key], 0);
                const prognoses_male_percentage: {[id:number]: number} = prognosis_tot_amount_male != 0 ? {
                    1: (prognoses_male_amount[1] / prognosis_tot_amount_male) * 100,
                    2: (prognoses_male_amount[2] / prognosis_tot_amount_male) * 100,
                    3: (prognoses_male_amount[3] / prognosis_tot_amount_male) * 100,
                    4: (prognoses_male_amount[4] / prognosis_tot_amount_male) * 100,
                    5: (prognoses_male_amount[5] / prognosis_tot_amount_male) * 100
                } : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0};

                const prognoses_female_amount: {[id:number]: number} = {
                    1: course['K']?.prognosis_1 || 0,
                    2: course['K']?.prognosis_2 || 0,
                    3: course['K']?.prognosis_3 || 0,
                    4: course['K']?.prognosis_4 || 0,
                    5: course['K']?.prognosis_5 || 0
                }
                
                const prognosis_tot_amount_female = prognosisArr.reduce((acc: number, key) => acc + prognoses_female_amount[key], 0);
                const prognoses_female_percentage: {[id:number]: number} = prognosis_tot_amount_female != 0 ? {
                    1: (prognoses_female_amount[1] / prognosis_tot_amount_female) * 100,
                    2: (prognoses_female_amount[2] / prognosis_tot_amount_female) * 100,
                    3: (prognoses_female_amount[3] / prognosis_tot_amount_female) * 100,
                    4: (prognoses_female_amount[4] / prognosis_tot_amount_female) * 100,
                    5: (prognoses_female_amount[5] / prognosis_tot_amount_female) * 100
                } : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0};

                const prognoses_total_amount: {[id:number]: number} = {
                    1: prognoses_male_amount[1] + prognoses_female_amount[1],
                    2: prognoses_male_amount[2] + prognoses_female_amount[2],
                    3: prognoses_male_amount[3] + prognoses_female_amount[3],
                    4: prognoses_male_amount[4] + prognoses_female_amount[4],
                    5: prognoses_male_amount[5] + prognoses_female_amount[5]
                }
                const prognosis_tot_amount_total = prognosisArr.reduce((acc: number, key) => acc + prognoses_total_amount[key], 0);
                const prognoses_total_percentage: {[id:number]: number} = prognosis_tot_amount_total != 0 ? {
                    1: (prognoses_total_amount[1] / prognosis_tot_amount_total) * 100,
                    2: (prognoses_total_amount[2] / prognosis_tot_amount_total) * 100,
                    3: (prognoses_total_amount[3] / prognosis_tot_amount_total) * 100,
                    4: (prognoses_total_amount[4] / prognosis_tot_amount_total) * 100,
                    5: (prognoses_total_amount[5] / prognosis_tot_amount_total) * 100
                } : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0};

                courses[courseId] = {
                    courseDisplayName: course.displayName,
                    prognoses_male_amount,
                    prognoses_male_percentage,
                    prognoses_female_amount,
                    prognoses_female_percentage,
                    prognoses_total_amount,
                    prognoses_total_percentage
                };
            }

            const courseColumns = Object.keys(courses).map((courseId, index) => {
                const course = courses[courseId];
                const column: ColumnDescription<any, any> = {
                    dataField: courseId,
                    text: course.courseDisplayName,
                    headerFormatter: (column: any, columnIndex: any) => {
                        return (<div style={{ width:'4rem', height: '14rem', margin: '0 auto', zIndex: 8 }} >
                            <div className="rotated-course-name" >
                                { column.text }
                            </div>
                        </div>);
                    },
                    formatter: (cell: any) => {
                        return (<div style={{ height: '100%', width: '100%', padding: '0.75rem' }} title={course.courseDisplayName}>
                            {cell}
                        </div>)
                    },
                    sort: false,
                    headerStyle: { width: '5.5rem', borderRightColor: 'transparent', zIndex: (10000 - index) },
                    style: { padding: 0, textAlign: 'center' },
                    classes: (cell, row) => `prognosis-bg-color-${row.prognosis}`,
                    headerClasses: "sticky-y"
                }

                return column;
            });
            
            const dataRowGroupings = [
                {label: 'Totalt antal elever', key: 'prognoses_total_amount', suffix: ''}, {label: 'Total andel elever', key: 'prognoses_total_percentage', suffix: '%'},
                {label: 'Kvinna antal elever', key: 'prognoses_female_amount', suffix: ''}, {label: 'Kvinna andel elever', key: 'prognoses_female_percentage', suffix: '%'},
                {label: 'Man antal elever', key: 'prognoses_male_amount', suffix: ''}, {label: 'Man andel elever', key: 'prognoses_male_percentage', suffix: '%'},
            ];

            let data = dataRowGroupings.map((elem) => {
                return prognosisArr.map((prognosis, index) => {
                    let courseData: any = {};

                    for(const courseId in courses) {
                        courseData[courseId] = parseFloat(courses[courseId][elem.key][prognosis].toFixed(1)) + elem.suffix;
                    }

                    return { 
                        name: index == 0 ? elem.label : '', 
                        prognosisName: Prognosis.enum[prognosis].name,
                        prognosis, 
                        ...courseData 
                    };
                });
            }).reduce((acc_arr, arr_elem) => acc_arr.concat([{prognosisName: arr_elem[0].name, subHeader: true }, ...arr_elem, {}]), [])
              .map((e, index) => ({...e, index}));

            this.setState({data, error: null, isLoading: false, courseColumns});
        }
        catch (error) {
            this.setState({error, isLoading: false, filtersModified: true});
        }
    }
    
    render() {
        const { 
            error, isLoading, data, startDate, endDate, filtersModified, courseColumns,
            courseIds, courseOptions,
            classIds, classOptions,
            schoolIds, schoolOptions,
            yearGrades, yearGradeOptions,
            programs, programOptions,
            teachers, teacherOptions
        } = this.state;

        const schoolIdArr = schoolIds.map(s => s.value);

        const columns : ColumnDescription<any, any>[] = [
            {
                dataField: 'index',
                text: 'Index',
                hidden: true
            },
            /*{
                dataField: 'name',
                text: '',
                searchable: false,
                headerStyle: {
                    width: '10rem',
                    textAlign: 'center',
                    wordBreak: 'break-word'
                }
            },*/
            {
                dataField: 'prognosisName',
                text: 'Prognos',
                searchable: false,
                headerStyle: {
                    width: '9rem',
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    zIndex: 15000
                },
                style: {
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    zIndex: 15000
                },
                headerClasses: 'sticky-x',
                classes: 'sticky-x'
            },
            ...courseColumns
        ];

        return (
            <div className="render-home">
                <Navigation role={this.props.role}/>
                <div className="junit-container">
                    <ErrorMessage hidden={error === null} style={{marginBottom:'1rem'}}>{error}</ErrorMessage>
                    <div style={{margin: '1rem 0'}}>
                        <div style={{ display: 'inline-block', padding: '0 1rem'}}>
                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Från:</span> 
                            <ReactDayPickerInput 
                                formatDate={(date) => date.toISOString().slice(0, 10)}
                                placeholder={'Välj datum..'}
                                inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                dayPickerProps={{
                                    locale: 'sv',
                                    months: months_long_se,
                                    weekdaysLong: weekdays_long_se,
                                    weekdaysShort: weekdays_short_se
                                }}
                                onDayChange={(e) => this.setState({startDate: e, filtersModified: true})}
                            />
                        </div>
                        <div style={{ display: 'inline-block', padding: '0 1rem'}}>
                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Till:</span> 
                            <ReactDayPickerInput 
                                formatDate={(date) => date.toISOString().slice(0, 10)}
                                placeholder={'Välj datum..'}
                                inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                dayPickerProps={{
                                    locale: 'sv',
                                    months: months_long_se,
                                    weekdaysLong: weekdays_long_se,
                                    weekdaysShort: weekdays_short_se
                                }}
                                onDayChange={(e) => this.setState({endDate: e, filtersModified: true})}
                            />
                        </div>
                        <div style={{ display: 'inline-flex', padding: '0 1rem', float: 'right' }}>
                            <Button disabled={!(startDate && endDate && filtersModified)} onClick={this.fetchData}>Hämta data</Button>
                        </div>
                    </div>
                    <div style={{borderTop: 'solid 1px lightgrey'}}>
                        <Row style={{width:'100%', margin: '0'}}>
                            <Col xs={12} sm={4} style={{padding: '1rem', zIndex: 16005}}>
                                <Multiselect
                                    value={courseIds}
                                    onChange={(selected: any) => this.setState({courseIds: selected, filtersModified: true})}
                                    options={courseOptions}
                                    //filterOptions={this.selectFilter}
                                    hasSelectAll={false}
                                    valueRenderer={() => "Filtrera kurser/ämnen"}
                                />
                            </Col>
                            <Col xs={12} sm={4} style={{padding: '1rem', zIndex: 16004}}>
                                <Multiselect
                                    value={classIds}
                                    onChange={(selected: any) => this.setState({classIds: selected, filtersModified: true})}
                                    options={/*schoolIdArr.length > 0 ? classOptions.filter(e => schoolIdArr.indexOf(e.schoolId) != -1) : */classOptions}
                                    //filterOptions={this.selectFilter}
                                    hasSelectAll={false}
                                    labelledBy={"Select"}
                                    valueRenderer={() => "Filtrera klasser"}
                                />
                            </Col>
                            <Col xs={12} sm={4} style={{padding: '1rem', zIndex: 16003}}>
                                <Multiselect
                                    value={schoolIds}
                                    onChange={(selected: any) => this.setState({schoolIds: selected, filtersModified: true})}
                                    options={schoolOptions}
                                    //filterOptions={this.selectFilter}
                                    hasSelectAll={false}
                                    labelledBy={"Select"}
                                    valueRenderer={() => "Filtrera skolor"}
                                />
                            </Col>
                        </Row>
                        <Row style={{width:'100%', margin: '0'}}>
                            <Col xs={12} sm={4} style={{padding: '1rem', paddingTop: 0, zIndex: 16002}}>
                                <Multiselect
                                    value={teachers}
                                    onChange={(selected: any) => this.setState({teachers: selected, filtersModified: true})}
                                    options={teacherOptions}
                                    valueRenderer={() => "Lärare"}
                                />
                            </Col>
                            <Col xs={12} sm={4} style={{padding: '1rem', paddingTop: 0, zIndex: 16001}}>
                                <Multiselect
                                    value={programs}
                                    onChange={(selected: any) => this.setState({programs: selected, filtersModified: true})}
                                    options={programOptions}
                                    valueRenderer={() => "Utbildningsväg"}
                                />
                            </Col>
                            <Col xs={12} sm={4} style={{padding: '1rem', paddingTop: 0, zIndex: 16000}}>
                                <Multiselect
                                    value={yearGrades}
                                    onChange={(selected: any) => this.setState({yearGrades: selected, filtersModified: true})}
                                    options={yearGradeOptions}
                                    //filterOptions={this.selectFilter}
                                    disableSearch
                                    valueRenderer={() => "Årskurs"}
                                />
                            </Col>
                        </Row>
                    </div>
                    <div style={{padding: '0.5rem', borderTop: 'solid 1px lightgrey'}}>
                        { courseIds.length == 0 && classIds.length == 0 && schoolIds.length == 0 
                          && programs.length == 0 && teachers.length == 0 && yearGrades.length == 0? 
                            <div style={{color:'grey', padding: '1rem'}}>Inga filter tillagda</div> 
                        : null}
                        {courseIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#755988', color:'white'}} 
                                onHide={() => this.removeCourseIdFilter(index)}
                            />
                        )}
                        {classIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#6176BD', color:'white'}} 
                                onHide={() => this.removeClassIdFilter(index)}
                            />
                        )}
                        {schoolIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#63B995', color:'white'}} 
                                onHide={() => this.removeSchoolIdFilter(index)}
                            />
                        )}
                        {teachers.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#F77F00', color:'white'}} 
                                onHide={() => this.removeTeacherFilter(index)}
                            />
                        )}
                        {programs.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#94A187', color:'white'}} 
                                onHide={() => this.removeProgramFilter(index)}
                            />
                        )}
                        {yearGrades.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#DF9216', color:'white'}} 
                                onHide={() => this.removeYearGradeFilter(index)}
                            />
                        )}
                    </div>
                    <div style={{borderTop: 'solid 1px lightgrey'}}>
                        <br/>
                        <div style={{maxWidth: '100%', overflowX: 'auto', height: '80vh'}}>
                            <div>
                                <Table 
                                    data={data}
                                    columns={columns}
                                    keyField={'index'}
                                    loading={isLoading}
                                    searchHidden
                                    bsTableProps={{
                                        rowStyle: (row: any, rowIndex: number) => {
                                            if(row.subHeader) {
                                                return { backgroundColor: '#F0F0F0', fontWeight: 'bold' }
                                            }
                                            else { return {} }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}